/**
 * profileAtoms.ts
 *
 * State atoms related to user profiles and authentication
 *
 * Primary responsibilities:
 * - Manage user profile data
 * - Handle authentication state
 * - Store user preferences
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { Tables } from '@/supabase/types'
import { getStorageForKey } from '@/lib/storage/storage-config'

// ===== User profile atoms =====
export const profileAtom = atom<Tables<'profiles'> | null>(null)

// Derived atoms for common profile fields
export const userIdAtom = atom((get) => get(profileAtom)?.id || null)

export const userEmailAtom = atom((get) => {
  const profile = get(profileAtom)
  return profile ? (profile as any).email || null : null
})

export const userDisplayNameAtom = atom((get) => get(profileAtom)?.display_name || null)

export const userAvatarUrlAtom = atom((get) => {
  const profile = get(profileAtom)
  return profile ? (profile as any).avatar_url || null : null
})

// ===== Authentication state atoms =====
export const isAuthenticatedAtom = atom((get) => get(profileAtom) !== null)

export const authTokenAtom = atomWithStorage<string | null>(
  'authToken',
  null,
  getStorageForKey('authToken')
)

// ===== User preferences atoms =====
export interface UserPreferences {
  theme: 'light' | 'dark' | 'system'
  fontSize: 'sm' | 'md' | 'lg' | 'xl'
  soundEnabled: boolean
  defaultLanguage: string | null
  showOnboarding: boolean
}

const defaultPreferences: UserPreferences = {
  theme: 'system',
  fontSize: 'md',
  soundEnabled: true,
  defaultLanguage: null,
  showOnboarding: true
}

export const userPreferencesAtom = atomWithStorage<UserPreferences>(
  'userPreferences',
  defaultPreferences,
  getStorageForKey('userPreferences')
)

// ===== Derived atoms =====

// Convenience atom for getting the current theme
export const themeAtom = atom(
  (get) => get(userPreferencesAtom).theme,
  (get, set, newTheme: 'light' | 'dark' | 'system') => {
    const prefs = get(userPreferencesAtom)
    set(userPreferencesAtom, { ...prefs, theme: newTheme })
  }
)

// Convenience atom for getting the current font size
export const fontSizeAtom = atom(
  (get) => get(userPreferencesAtom).fontSize,
  (get, set, newSize: 'sm' | 'md' | 'lg' | 'xl') => {
    const prefs = get(userPreferencesAtom)
    set(userPreferencesAtom, { ...prefs, fontSize: newSize })
  }
)

// ===== Recent activity atoms =====
export const lastActiveAtom = atomWithStorage<string | null>(
  'lastActive',
  null,
  getStorageForKey('lastActive')
)

export const lastVisitedPageAtom = atomWithStorage<string | null>(
  'lastVisitedPage',
  null,
  getStorageForKey('lastVisitedPage')
)

// ===== Profile settings UI state =====
export const isProfileSettingsOpenAtom = atom<string>('')

// Theme preference atom
export const themePreferenceAtom = atom<'light' | 'dark' | 'system'>((get) => {
  const prefs = get(userPreferencesAtom)
  return prefs.theme
})

// ===== Helper functions =====

// Update profile - updated for proper usage with atoms
export const updateProfile = (profile: Tables<'profiles'>) => {
  return profile
}

// Update preference - updated for proper usage with atoms
export const updatePreference = <K extends keyof UserPreferences>(
  key: K,
  value: UserPreferences[K],
  currentPrefs: UserPreferences
): UserPreferences => {
  return {
    ...currentPrefs,
    [key]: value
  }
}

// Update preferences - updated for proper usage with atoms
export const updatePreferences = (
  updates: Partial<UserPreferences>,
  currentPrefs: UserPreferences
): UserPreferences => {
  return {
    ...currentPrefs,
    ...updates
  }
}

// Reset preferences to defaults - updated for proper usage with atoms
export const resetPreferences = (): UserPreferences => {
  return defaultPreferences
}

// Derived profile atoms
export const displayNameAtom = atom(
  (get) => get(profileAtom)?.display_name || '',
  (get, set, newName: string) => {
    const profile = get(profileAtom)
    if (profile) {
      set(profileAtom, {
        ...profile,
        display_name: newName
      })
    }
  }
)

export const profileImageUrlAtom = atom(
  (get) => get(profileAtom)?.image_url || '',
  (get, set, newUrl: string) => {
    const profile = get(profileAtom)
    if (profile) {
      set(profileAtom, {
        ...profile,
        image_url: newUrl
      })
    }
  }
)

// API keys atom - derived from profile
export const apiKeysAtom = atom((get) => {
  const profile = get(profileAtom)
  if (!profile) return {}

  const keys: Record<string, string> = {}

  if (profile.anthropic_api_key) keys.anthropic = profile.anthropic_api_key
  if (profile.openai_api_key) keys.openai = profile.openai_api_key
  if (profile.google_gemini_api_key) keys.google = profile.google_gemini_api_key
  if (profile.azure_openai_api_key) keys.azure = profile.azure_openai_api_key
  if (profile.mistral_api_key) keys.mistral = profile.mistral_api_key
  if (profile.groq_api_key) keys.groq = profile.groq_api_key
  if (profile.perplexity_api_key) keys.perplexity = profile.perplexity_api_key
  if (profile.openrouter_api_key) keys.openrouter = profile.openrouter_api_key

  return keys
})

// Timezone state
export const isTimezoneSetupAtom = atom(false)
